<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const hasLoaded = ref(false);
const loadError = ref('');
const creditLines = ref([]);

const loadBilling = async () => {
  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppBilling(props.inbox.id);
    creditLines.value = response.data?.payload || [];
    loadError.value = '';
    hasLoaded.value = true;
  } catch (error) {
    loadError.value =
      error?.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BILLING_LOAD_ERROR');
    useAlert(loadError.value);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.active,
  isActive => {
    if (isActive && !hasLoaded.value) loadBilling();
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid gap-4">
    <div class="flex justify-end">
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-ph-arrows-clockwise"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BILLING_REFRESH')"
        :is-loading="isLoading"
        @click="loadBilling"
      />
    </div>

    <div
      v-if="isLoading"
      class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BILLING_LOADING') }}
    </div>

    <div
      v-else-if="loadError"
      class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-6 text-sm text-n-ruby-11"
    >
      {{ loadError }}
    </div>

    <div v-else class="grid gap-3">
      <div
        v-for="line in creditLines"
        :key="line.id"
        class="rounded-xl border border-n-container bg-n-alpha-2 p-4"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ line.owner_business_name || line.legal_entity_name || line.id }}
            </p>
            <p class="mt-1 mb-0 text-xs text-n-slate-11">
              {{ line.status }} · {{ line.currency }} ·
              {{ line.allocated_amount || '—' }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="!creditLines.length && hasLoaded"
        class="rounded-xl border border-dashed border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BILLING_EMPTY') }}
      </div>
    </div>
  </div>
</template>
